import type { CSSProperties, ReactElement } from "react";
import type { KundeListe as KundeListeEintrag, PartnerRole, Unternehmen, WeitererTreffer, Zuordnung }
  from "@kundeninfo/api-client";
import { Icon, Objektliste, Objektzeile, istFarbwert, useSofortPopup } from "@kundeninfo/ui";
import { Knopf, Pillenreihe, Schalter } from "@kundeninfo/ui/me-design";
import { hausfarbe } from "../markenfarben.js";
import { relativeZeit } from "../format.js";
import { KiKnopf, Suchfeld } from "../components/meHuellen.js";
import { useVerzoegerteSuche } from "../components/verzoegerteSuche.js";
import { kennzeichenFolge } from "../components/objektPanel/kennzeichen.js";
import { PARTNER_TEXT } from "./partners/texts.js";
import { partnerBadgeKeys, partnerBadges } from "./partners/PartnerRoleBadges.js";
import { ROLLEN, schalteRolle } from "./partners/rollenFilter.js";
import type { RollenWahl } from "./partners/rollenFilter.js";
import "./KundenListe.css";

/** Schmales Fehlerbild — passt sowohl eine ApiError-Instanz als auch ein Test-Double. */
export type KundenFehler = { message: string; code?: string; incidentId?: string };

export type KundenListeProps = {
  items: KundeListeEintrag[];
  loading: boolean;
  error: KundenFehler | null;
  search: string;
  onSearch: (value: string) => void;
  selectedId: string | null;
  onSelect: (id: string) => void;
  /** Der Einstieg zum Anlegen eines Kunden (Aufgabe 17). Ohne Angabe fehlt der Knopf. */
  onNeu?: () => void;
  /** Für die Kürzel-Chips in Markenfarbe. Ohne Angabe erscheinen sie neutral. */
  companies?: Unternehmen[];
  /**
   * Die DOM-Id des Suchfelds. Sie ist wählbar, seit die Liste ein ZWEITES Mal im selben
   * Dokument stehen kann (im Eltern-Modal des Konzern-Reiters): Zwei Elemente mit
   * derselben Id sind eines zu viel — `document.getElementById` träfe das falsche, und
   * ein Prüfer meldete das Dokument zu Recht als ungültig.
   */
  feldId?: string;
  /** Treffer der Suche in ANDEREN Unternehmen (Mandantentrennung R22) — nur Zahlen. */
  weitereTreffer?: readonly WeitererTreffer[];
  /** „Zu <Kürzel> wechseln" an der Trefferzeile. */
  onWechseln?: (companyId: string) => void;
  /**
   * Die Rollen-Chips der Geschäftspartnerliste (Festlegung C9): „Kunden“, „Interessenten“, „Lieferanten“ und der
   * Schalter „ehemalige zeigen“. Gehalten wird die Wahl von App (gemerkt im Browser, `rollenFilter.ts`); ohne Angabe
   * fehlt die Leiste — so in der Auswahl des Elternkunden (KundeKonzern), die keine Rollen filtert.
   */
  rollenWahl?: RollenWahl;
  onRollenWahl?: (w: RollenWahl) => void;
  /** Das aktive Unternehmen: Seine Beziehung liefert Nummern und Rollen der Zeile (die Liste filtert über sie, P3).
   *  Ohne Angabe die erste Beziehung. */
  aktivId?: string | null;
};

const text = (wert: string | null | undefined): string | null => (wert === null || wert === undefined || wert === "" ? null : wert);

/** „Tochter von …“ — nur bei EINSEHBAREM Elternteil (der Server lässt `parentName` sonst leer). */
function tochterVon(item: KundeListeEintrag): string[] {
  const eltern = text(item.parentName);
  return eltern === null ? [] : [`Tochter von ${eltern}`];
}

/** Der Ort als Teil der Zeile — leer, wenn die Firma keinen hat (dann entfällt er, es steht kein Platzhalter). */
function ortTeil(item: KundeListeEintrag): string[] {
  const ort = text(item.city);
  return ort === null ? [] : [ort];
}

/** Ort und Konzernhinweis: ein Teil des zugänglichen Namens der Zeile (`rowParts`). */
function zweiteZeile(item: KundeListeEintrag): string[] {
  return [...ortTeil(item), ...tochterVon(item)];
}

/**
 * Die Ortszeile (Zeile 2, ein leiser Text): „‹Ort› · ‹Kundennummer› · ‹Kreditornummer›“ und der Konzernhinweis am Ende.
 * Die Rollen stehen NICHT im Text, sondern als Kürzel-Kennzeichen dahinter (Entscheide 525a, 110). Jeder Teil steht nur, wenn er einen
 * Wert hat (kein „–“). Die Kundennummer nur mit Kundenrolle (nach dem Entfernen der Rolle bleibt sie in der Lese-Gestalt
 * stehen, C7), die Kreditornummer nur mit Lieferantenrolle.
 */
function metaZeile(item: KundeListeEintrag, sicht: Zuordnung | undefined): string {
  const n = numbers(sicht);
  const numberParts = [n.customer, n.creditor].filter((nummer): nummer is string => nummer !== null);
  return [...ortTeil(item), ...numberParts, ...tochterVon(item)].join(" · ");
}

/** Kundennummer (nur mit Kundenrolle) und Kreditornummer (nur mit Lieferantenrolle) der gezeigten Beziehung. */
function numbers(sicht: Zuordnung | undefined): { customer: string | null; creditor: string | null } {
  return {
    customer: sicht === undefined || sicht.status == null ? null : text(sicht.customerNumber),
    creditor: sicht === undefined || sicht.supplierStatus == null ? null : text(sicht.creditorNumber),
  };
}

/** Der zugängliche Name einer Zeile (Final-Fixrunde A-K4): was die Zeile zeigt — Firma, Ort, Konzern, Rollen und
 *  Nummern —, damit die Vorlesestimme dieselben Angaben hat wie das Auge. */
function rowParts(item: KundeListeEintrag, sicht: Zuordnung | undefined, roles: readonly string[]): string[] {
  const n = numbers(sicht);
  return [item.companyName, ...zweiteZeile(item), ...roles,
    ...(n.customer === null ? [] : [PARTNER_TEXT.customerNumberValue(n.customer)]),
    ...(n.creditor === null ? [] : [PARTNER_TEXT.creditorValue(n.creditor)])];
}

/** Ein Rollen-Chip: Umschalt-Pille in den Maßen der Chip-Tokens (Entscheid 670) mit fester Breite je Rolle (Hausregel „Leiste: kein
 *  Layoutwechsel“, `kl__rollenchip--‹Rolle›` in KundenListe.css). Der letzte gewählte bleibt an; sein Sofort-Popup sagt warum. */
function RollenChip(p: { rolle: PartnerRole; wahl: RollenWahl; onWahl: (w: RollenWahl) => void }): ReactElement {
  const an = p.wahl.roles.includes(p.rolle);
  const letzter = an && p.wahl.roles.length === 1;
  const hinweis = useSofortPopup<HTMLElement>(letzter ? PARTNER_TEXT.atLeastOne : "");
  return (
    <Knopf ref={hinweis} className={`kl__rollenchip kl__rollenchip--${p.rolle.toLowerCase()}`} art="sekundaer"
      gestalt="pille" gedrueckt={an}
      onClick={() => { if (!letzter) p.onWahl(schalteRolle(p.wahl, p.rolle)); }}>
      {PARTNER_TEXT.chips[p.rolle]}
    </Knopf>
  );
}

/** Die Leiste der Rollen-Chips samt Schalter „ehemalige zeigen“ (C9) — zeichnet und meldet, hält keinen Zustand. Die
 *  Chips stehen in einer Zeile, der Schalter in der eigenen Zeile darunter (Entscheid 523). */
function RollenLeiste(p: { wahl: RollenWahl; onWahl: (w: RollenWahl) => void }): ReactElement {
  return (
    <div className="kl__rollen" role="group" aria-label={PARTNER_TEXT.chipsLabel}>
      <Pillenreihe className="kl__rollenreihe">
        {ROLLEN.map((r) => <RollenChip key={r} rolle={r} wahl={p.wahl} onWahl={p.onWahl} />)}
      </Pillenreihe>
      <Schalter className="kl__ehemalige" beschriftung={PARTNER_TEXT.showFormer} an={p.wahl.includeFormer}
        onGeaendert={(d) => p.onWahl({ ...p.wahl, includeFormer: d.an })} />
    </div>
  );
}

export function KundenListe(p: KundenListeProps): ReactElement {
  const companies = p.companies ?? [];
  const onWechseln = p.onWechseln;
  const weitere = p.search.trim() === "" || p.loading || p.error !== null ? [] : p.weitereTreffer ?? [];
  // Ohne Leiste (Konzern-Auswahl) gilt die Vorgabe des Servers ohne `role`: Ehemalige gehören dazu (P3).
  const mitEhemaligen = p.rollenWahl?.includeFormer ?? true;
  // Verzögert melden, sofort zeigen; ein Zurücksetzen von außen zieht das Feld nach (components/verzoegerteSuche.ts).
  const [eingabe, setEingabe] = useVerzoegerteSuche(p.search, p.onSearch);

  return (
    <div className="kl">
      <div className="kl__suche">
        {/* ME-Etappe 4, P4: Suchfeld `me-eingabe art="suche"`, „Neuer Geschäftspartner“ `me-knopf`. */}
        <Suchfeld id={p.feldId ?? "kl-suchfeld"} name={PARTNER_TEXT.searchLabel}
          platzhalter="Firma oder Ort suchen" wert={eingabe} onText={setEingabe} />
        {p.onNeu !== undefined && (
          <KiKnopf art="primaer" className="kl__neu" onClick={p.onNeu}>{PARTNER_TEXT.newPartner}</KiKnopf>
        )}
      </div>

      {p.rollenWahl !== undefined && p.onRollenWahl !== undefined && (
        <RollenLeiste wahl={p.rollenWahl} onWahl={p.onRollenWahl} />
      )}

      {p.error !== null && (
        <div className="kl__fehler" role="alert">
          <Icon name="triangle-exclamation" size={16} label="Fehler" />
          <span>{p.error.message}</span>
        </div>
      )}

      {/* Gegenstück zu role="alert" oben: role="status" kündigt sich Screenreadern von
          selbst an (implizites aria-live="polite"). p.loading kommt ausschließlich aus der
          echten Ladeoperation in App.tsx (dort erst nach der 250-ms-Verzögerung dieser
          Komponente gesetzt) — der Hinweis flackert deshalb nicht bei jedem Tastendruck. */}
      {p.error === null && p.loading && (
        <p className="kl__lade" role="status">
          <span className="kl__lade-spinner" aria-hidden="true" />
          <span>Lädt …</span>
        </p>
      )}

      {p.error === null && !p.loading && p.items.length === 0 && (
        <p className="kl__hinweis">{PARTNER_TEXT.notFound}</p>
      )}

      {/* Die Treffer als Objektliste (ME-Etappe 4, P4): Auswahlzeile nach der Anatomie (Entscheide 110, 112, „Öffnen statt
          Springen“ K14). Name in Zeile 1; Ort, Nummern und Konzernhinweis als `meta`; dahinter in derselben zweiten Zeile
          links die Kennzeichen (Slot `chips`) in der festen Folge Zugehörigkeit → Rollen: Unternehmenskürzel in der
          Hausfarbe, dann die Rollen-Kürzel nach 525a (`kennzeichenFolge`). Rechts nur noch die letzte Aktivität (Zeit).
          Roving Tabindex und Pfeiltasten macht das Element. Der zugängliche Name trägt Rollen und Konzernhinweis als
          WORT („Tochter von …“) — nur bei EINSEHBAREM Elternteil (Festlegung F1, Punkt 5), der Server lässt `parentName`
          sonst leer. Hausregel 542: Text und Zeit stehen oben (`ausrichtung="oben"`). */}
      {p.items.length > 0 && (
        <div className="kl__treffer">
          <Objektliste label={PARTNER_TEXT.navLabel}>
            {p.items.map((item) => {
              const sicht = item.relations.find((r) => r.companyId === p.aktivId) ?? item.relations[0];
              const roles = sicht === undefined ? [] : partnerBadges(sicht, mitEhemaligen);
              const marken = kennzeichenFolge({ unternehmen: item.relations.map((r) => r.shortCode),
                rollen: sicht === undefined ? [] : partnerBadgeKeys(sicht, mitEhemaligen), rollenText: PARTNER_TEXT.roleShort },
              companies);
              return (
                <Objektzeile key={item.id} name={item.companyName} ausrichtung="oben"
                  meta={metaZeile(item, sicht)} marken={marken}
                  gewaehlt={item.id === p.selectedId}
                  ariaLabel={PARTNER_TEXT.rowName(rowParts(item, sicht, roles))}
                  onOeffnen={() => p.onSelect(item.id)}>
                  <span className="kl__aktivitaet">{relativeZeit(item.lastActivityAt)}</span>
                </Objektzeile>
              );
            })}
          </Objektliste>
        </div>
      )}

      {/* Die WEITEREN TREFFER (Spec Mandantentrennung, E370, R22): unter der Liste, auch unter
          „Kein Geschäftspartner gefunden.", nur bei Suchtext und nicht beim Laden oder nach einem Fehler.
          Nur Kürzel und Zahl — der Wechsel geht über den Wechselweg der Anwendung (Verlassen-
          Wächter, Neuladen) und nimmt die Suche mit. Die Kürzel stehen als ruhiger Text in Hausfarbe,
          nicht als Chip (Dirk 393); die Schrift mischt KundenListe.css aus `--kl-hausfarbe`. */}
      {weitere.length > 0 && onWechseln !== undefined && (
        <div className="kl__weitere" role="group" aria-label="Weitere Treffer in anderen Unternehmen">
          <span className="kl__weitere-text">
            Weitere Treffer:{" "}
            {weitere.map((t, i) => (
              <span key={t.companyId}>
                {i > 0 && ", "}
                <span className="kl__kuerzel" style={kuerzelFarbe(companies, t.shortCode)}>{t.shortCode}</span>
                {` (${t.count})`}
              </span>
            ))}
          </span>
          {weitere.map((t) => (
            <KiKnopf key={t.companyId} art="leise" onClick={() => onWechseln(t.companyId)}>
              Zu {t.shortCode} wechseln
            </KiKnopf>
          ))}
        </div>
      )}
    </div>
  );
}

/** Die Hausfarbe eines Kürzels als Eigenschaft `--kl-hausfarbe` (Dirk 393) — nur ein gültiger Hexwert, sonst bleibt
 *  die Schrift ruhig (wie me-chip einen krummen Wert neutral lässt). */
function kuerzelFarbe(companies: readonly Unternehmen[] | undefined, shortCode: string): CSSProperties | undefined {
  const farbe = hausfarbe(companies, shortCode);
  return istFarbwert(farbe) ? ({ "--kl-hausfarbe": farbe } as CSSProperties) : undefined;
}
